import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../services/premium-toast.service';
import { ReasoningService, ReasoningLevel } from '../../../services/reasoning.service';
import { ChatFireService } from '../../../gen2ai/chatfire.service';
import {
  CLARI_CHAT_MODEL_CATEGORIES,
  isClariSignedInOnlyCatalogModel
} from '../../../shared/clari-chat-model-catalog';

type ChatGpt52ReasoningLevel = Exclude<ReasoningLevel, 'auto'>;

type CatalogModel = { name?: string; label?: string; value: string; icon?: string };
type CatalogCategory = { title: string; models: CatalogModel[] };
type CatalogModelDetails = {
  id: string;
  name: string;
  vendor: string;
  shortBio: string;
  bio: string;
  strengths: string[];
  bestFor: string[];
};

@Component({
  selector: 'app-ai-catalog',
  templateUrl: './ai-catalog.component.html',
  styleUrls: ['./ai-catalog.component.scss']
})
export class AiCatalogComponent implements AfterViewInit, OnInit, OnDestroy {
  categories: CatalogCategory[] = [];
  private readonly initialSelectedValue?: string;

  isSignedInUser = false;
  private authSub?: Subscription;

  readonly gpt52ReasoningOptions: Array<{ value: ChatGpt52ReasoningLevel; label: string }> = [
    { value: 'instant', label: 'Instant' },
    { value: 'thinking', label: 'Thinking' },
    { value: 'pro', label: 'Pro' }
  ];

  selectedGpt52ReasoningLevel: ChatGpt52ReasoningLevel = 'instant';

  @ViewChild('selectionBar') selectionBar!: ElementRef<HTMLElement>;

  getModelName(model?: CatalogModel): string {
    const normalizedValue = this.normalizeChatGptValue(model?.value);
    const canonicalName = this.getCatalogDetailsByValue(normalizedValue)?.name;
    return (canonicalName || model?.name || model?.label || '').trim();
  }

  /** True when the model id is Cohere Command Light (shown as ⌘ + Instruct in the UI). */
  isCommandLightCatalogModel(model?: CatalogModel | null): boolean {
    if (!model?.value) {
      return false;
    }
    return this.normalizeChatGptValue(model.value) === 'commandlight';
  }

  /** True when the model id resolves to Cohere R+ (shown as Cohere ⌘ R+ in bio). */
  isCohereRPlusCatalogModel(model?: CatalogModel | null): boolean {
    if (!model?.value) {
      return false;
    }
    return this.normalizeChatGptValue(model.value) === 'cohere';
  }

  /** Selection bar: show ⌘ + Instruct when the resolved choice is Command Light (including legacy labels). */
  showCommandInstructSelectionLabel(): boolean {
    if (this.visibleSelectedModel) {
      return this.isCommandLightCatalogModel(this.visibleSelectedModel);
    }
    const incoming = this.data?.selectedModel;
    if (incoming != null && this.normalizeChatGptValue(String(incoming)) === 'commandlight') {
      return true;
    }
    const nm = (this.visibleSelectedModelName || '').trim().toLowerCase();
    return nm === 'command instruct';
  }

  get visibleSelectedModel(): CatalogModel | undefined {
    return this.selectedModel && !this.isRemovedModel(this.selectedModel)
      ? this.selectedModel
      : undefined;
  }

  get visibleSelectedModelName(): string | null {
    if (this.visibleSelectedModel) {
      return this.getModelName(this.visibleSelectedModel);
    }

    const incomingValue = this.data?.selectedModel;
    const incomingName = (this.data?.selectedModelName || '').trim();

    if (incomingValue && this.isRemovedModelValue(incomingValue)) {
      return null;
    }

    if (incomingName && !this.isRemovedModelName(incomingName)) {
      return incomingName;
    }

    return null;
  }

  private readonly removedModelValues = new Set<string>([
    'chatgpt54',
    'minichatgpt54',
    'jambainstruct',
    'llama31',
    'llama3.1',
    'llama-3.1',
    'llama_3_1',
    'llama3_1',
    'commandr',
    'command-r',
    'commandrplus',
    'command-r-plus',
    'command-r+'
  ]);

  private readonly removedModelNameMatchers: string[] = [
    'chatgpt 5 4',
    'chatgpt mini',
    'claude opus 4 5',
    'amazon nova',
    'nova premier',
    'llama 3 1',
    'command r'
  ];

  /**
   * Models intentionally hidden from the visible picker grid while preserving
   * underlying service/component support and catalog metadata.
   */
  private readonly sidelinedModelValues = new Set<string>([
    'pegasus',
    'qwen'
  ]);

  private readonly sidelinedModelNameMatchers: string[] = [
    'pegasus 1 2'
  ];

  private readonly defaultCategories: CatalogCategory[] = [
    ...CLARI_CHAT_MODEL_CATEGORIES.map(category => ({
      title: category.title,
      models: category.models.map(model => ({
        name: model.name,
        value: model.value
      }))
    }))
  ];

  private readonly modelCatalog: Record<string, CatalogModelDetails> = {
    chatgpt: {
      id: 'chatgpt',
      name: 'ChatGPT 5.4',
      vendor: 'OpenAI',
      shortBio:
        'OpenAI’s flagship general-purpose model for complex reasoning, polished writing, and production-grade code.',
      bio:
        'ChatGPT 5.4 is designed to move fluidly between analysis, software engineering, drafting, and tool-guided workflows without needing a different model for each task. It is especially strong when you want structured answers, clean instruction-following, and dependable performance across long, multi-step conversations.',
      strengths: [
        'Deep multi-step reasoning',
        'Strong coding and refactoring ability',
        'Clear instruction adherence',
        'Stable long-context conversations'
      ],
      bestFor: [
        'Complex problem solving',
        'Production-quality code generation',
        'Detailed research summaries',
        'General-purpose expert assistance'
      ]
    },
    sonnet45: {
      id: 'sonnet45',
      name: 'Claude Sonnet 4.5',
      vendor: 'Anthropic',
      shortBio:
        'A balanced Claude model that blends thoughtful reasoning, strong writing, and dependable coding support.',
      bio:
        'Claude Sonnet 4.5 is a strong all-around Claude model aimed at teams that want high answer quality without the latency or cost profile of the heaviest frontier models. It performs well in analysis, drafting, brainstorming, and engineering support, while also staying conversational and easy to steer.',
      strengths: [
        'Balanced quality and speed',
        'Strong writing tone control',
        'Reliable coding assistance',
        'Good conversational alignment'
      ],
      bestFor: [
        'General chat and drafting',
        'Software design discussions',
        'Summaries and synthesis',
        'Creative and business writing'
      ]
    },
    opus46: {
      id: 'opus46',
      name: 'Claude Opus 4.6',
      vendor: 'Anthropic',
      shortBio:
        'Flagship Claude on Amazon Bedrock (Converse API) — deep reasoning, long-context analysis, and elite coding reliability.',
      bio:
        'Claude Opus 4.6 is Anthropic’s frontier model on Bedrock, configured for high-stakes analysis, long-form drafting, and complex problem solving where accuracy and sustained coherence matter more than raw speed.',
      strengths: [
        'High-end reasoning quality',
        'Rich long-form responses',
        'Strong synthesis across context',
        'Useful for complex problem solving'
      ],
      bestFor: [
        'Deep analysis',
        'Long-form drafting',
        'High-stakes reasoning tasks',
        'Detailed strategic thinking'
      ]
    },
    opus47: {
      id: 'opus47',
      name: 'Claude Opus 4.7',
      vendor: 'Anthropic',
      shortBio:
        'Next-generation Claude Opus on Bedrock — tuned for frontier reasoning, longer coherent threads, and demanding agent-style workflows.',
      bio:
        'Claude Opus 4.7 extends the Opus line with stronger end-to-end reasoning, improved tool use and instruction following, and higher reliability on long, multi-step tasks. It is aimed at the same high-stakes workloads as Opus 4.6 with additional headroom for depth, nuance, and sustained context use.',
      strengths: [
        'Frontier-grade reasoning',
        'Strong long-thread coherence',
        'Elite coding and systems thinking',
        'High instruction fidelity'
      ],
      bestFor: [
        'Agentic and multi-step workflows',
        'Architecture and code reviews',
        'Long-form analysis and synthesis',
        'High-stakes decision support'
      ]
    },
    kimik25: {
      id: 'kimik25',
      name: 'Kimi 2.5',
      vendor: 'Moonshot AI',
      shortBio:
        'Kimi 2.5 — long-context, multilingual chat tuned for reasoning, coding, and everyday expert assistance.',
      bio:
        'Kimi 2.5 is Moonshot AI’s advanced conversational model, well suited to long prompts, technical Q&A, and structured answers across languages. In ClariChat it is served through a dedicated Lambda integration so you get consistent latency and the same prompt-plus-history shape as other catalog models.',
      strengths: [
        'Long-context friendly responses',
        'Solid multilingual coverage',
        'Good balance of speed and depth',
        'Strong generalist assistant behavior'
      ],
      bestFor: [
        'Research and drafting',
        'Coding help and debugging',
        'Business and technical summaries',
        'Everyday high-quality chat'
      ]
    },
    qwen: {
      id: 'qwen',
      name: 'Qwen 3 Coder',
      vendor: 'Qwen',
      shortBio:
        'Alibaba’s coding-focused Qwen model tuned for implementation, debugging, and structured technical answers.',
      bio:
        'Qwen 3 Coder is designed for software workflows where strong code generation, practical debugging, and consistent technical structure are critical. It performs well across implementation tasks, refactors, and developer Q&A while staying useful for general text reasoning.',
      strengths: [
        'Strong code generation quality',
        'Reliable debugging support',
        'Structured technical explanations',
        'Balanced reasoning and implementation'
      ],
      bestFor: [
        'Feature implementation',
        'Bug investigation',
        'Code reviews and refactors',
        'Technical assistant workflows'
      ]
    },
    glm5: {
      id: 'glm5',
      name: 'GLM-5',
      vendor: 'Zhipu AI',
      shortBio:
        'A multilingual reasoning model from Zhipu AI built for structured answers, longer-form responses, and broad general chat.',
      bio:
        'GLM-5 is a capable text-first model that fits well in multilingual environments and workflows that need solid reasoning without overly terse answers. It is well suited to research summaries, general-purpose assistance, and longer responses where clarity and structure matter.',
      strengths: [
        'Multilingual output quality',
        'Solid long-form responses',
        'Structured explanations',
        'Balanced general-purpose chat'
      ],
      bestFor: [
        'General chat',
        'Research and summarization',
        'Longer written answers',
        'Multilingual workflows'
      ]
    },
    palmx: {
      id: 'palmx',
      name: 'Writer X5',
      vendor: 'Writer',
      shortBio:
        'An enterprise model geared toward huge context windows, governed outputs, and document-heavy workflows.',
      bio:
        'Writer X5 is designed for organizations that need consistent, structured performance across large documents, policy-heavy tasks, and workflow automation. It shines when the job involves long context, predictable formatting, and business-oriented outputs rather than purely conversational use.',
      strengths: [
        'Very large context handling',
        'Structured, policy-friendly outputs',
        'Strong workflow consistency',
        'Useful document-level reasoning'
      ],
      bestFor: [
        'Long-document analysis',
        'Workflow automation',
        'Enterprise writing tasks',
        'Policy and compliance support'
      ]
    },
    gemini: {
      id: 'gemini',
      name: 'Gemini 3',
      vendor: 'Google',
      shortBio:
        'A fast multimodal model that is especially handy for synthesis, broad knowledge tasks, and responsive assistance.',
      bio:
        'Gemini 3 is a strong option when you want a model that feels quick, broadly knowledgeable, and capable of handling multimodal or research-adjacent tasks. It is well suited to summaries, rapid Q&A, and general analytical workflows where speed and breadth matter.',
      strengths: [
        'Fast response profile',
        'Broad knowledge coverage',
        'Multimodal readiness',
        'Strong summarization'
      ],
      bestFor: [
        'Fast Q&A',
        'Research briefs',
        'General summaries',
        'Everyday assistant tasks'
      ]
    },
    pixtralimage: {
      id: 'pixtralimage',
      name: 'Pixtral Large',
      vendor: 'Mistral',
      shortBio:
        'A premium vision-language model built for high-resolution image understanding and multimodal reasoning.',
      bio:
        'Pixtral Large 25.02 is aimed at tasks where image comprehension matters as much as language quality. It performs well on visual Q&A, document understanding, image-grounded analysis, and workflows where you want a model to reason over both images and text together.',
      strengths: [
        'High-resolution image understanding',
        'Strong multimodal reasoning',
        'Good document and diagram analysis',
        'Stable visual Q&A performance'
      ],
      bestFor: [
        'Document parsing',
        'Visual analysis',
        'Image-grounded chat',
        'Creative asset review'
      ]
    },
    pegasus: {
      id: 'pegasus',
      name: 'Pegasus 1.2',
      vendor: 'TwelveLabs',
      shortBio:
        'A multimodal understanding model tuned for image/video-grounded reasoning with structured output support.',
      bio:
        'Pegasus 1.2 is designed for media-aware prompting where an attached visual reference steers the response. It supports prompt-plus-history chat with optional schema-constrained output in the same request contract, making it useful for consistent production pipelines.',
      strengths: [
        'Media-grounded reasoning',
        'Schema-friendly structured outputs',
        'Reliable prompt + history chat behavior',
        'Strong fit for visual analysis workflows'
      ],
      bestFor: [
        'Reference-image guided responses',
        'Structured media extraction',
        'Visual QA and analysis',
        'Production multimodal assistants'
      ]
    },
    mistrallarge: {
      id: 'mistrallarge',
      name: 'Mistral Large',
      vendor: 'Mistral',
      shortBio:
        'A premium vision-language model built for high-resolution image understanding and multimodal reasoning.',
      bio:
        'Mistral Large is aimed at tasks where image comprehension matters as much as language quality. It performs well on visual Q&A, document understanding, image-grounded analysis, and workflows where you want a model to reason over both images and text together.',
      strengths: [
        'High-resolution image understanding',
        'Strong multimodal reasoning',
        'Good document and diagram analysis',
        'Stable visual Q&A performance'
      ],
      bestFor: [
        'Document parsing',
        'Visual analysis',
        'Image-grounded chat',
        'Creative asset review'
      ]
    },
    codestralto: {
      id: 'codestralto',
      name: 'Codestral',
      vendor: 'Mistral',
      shortBio:
        'Mistral’s code-specialized model tuned for software development: implementation, refactors, reviews, and technical explanations.',
      bio:
        'Codestral is built for day-to-day engineering work—turning specs into code, debugging, explaining APIs, and keeping answers structured for developers. It pairs well with Clari when you want a model that prioritizes correctness, idioms, and maintainable patterns over generic chat.',
      strengths: [
        'Strong coding and refactoring',
        'Clear technical explanations',
        'Good structure for reviews and diffs',
        'Solid instruction-following for dev tasks'
      ],
      bestFor: [
        'Implementation and bug fixes',
        'Code review style feedback',
        'API and library questions',
        'Refactors and test scaffolding'
      ]
    },
    minichatgpt54: {
      id: 'minichatgpt54',
      name: 'ChatGPT Mini',
      vendor: 'OpenAI',
      shortBio:
        'A lighter ChatGPT 5.4 route optimized for fast, cost-efficient replies while staying on the same family of capabilities.',
      bio:
        'ChatGPT Mini is ideal when you want responsive turn-taking, quick drafts, and everyday assistance without always paying the full cost of the largest 5.4 profile. Use it for chatty workflows, short analyses, and rapid iteration; switch to full ChatGPT 5.4 when you need maximum depth.',
      strengths: [
        'Lower latency feel for chat',
        'Efficient everyday assistance',
        'Same product line as ChatGPT 5.4',
        'Good for high-volume messaging'
      ],
      bestFor: [
        'Quick Q&A and drafting',
        'Brainstorming and outlines',
        'Casual multi-turn chat',
        'When speed matters more than max reasoning'
      ]
    },
    deepv3: {
      id: 'deepv3',
      name: 'DeepSeek V3.2',
      vendor: 'DeepSeek',
      shortBio:
        'A strong generalist model known for long-context capability, technical reasoning, and cost-efficient performance.',
      bio:
        'DeepSeek V3.2 is a capable mixture-of-experts model that performs well across coding, analysis, and general productivity tasks. It is often a strong choice when you want serious reasoning and long-context utility without always reaching for a more expensive frontier model.',
      strengths: [
        'Strong technical reasoning',
        'Long-context stability',
        'Good coding support',
        'Cost-conscious performance'
      ],
      bestFor: [
        'Engineering workflows',
        'Long-context analysis',
        'General-purpose chat',
        'Technical drafting'
      ]
    },
    maverick: {
      id: 'maverick',
      name: 'Llama 4',
      vendor: 'Meta',
      shortBio:
        'An open-weight flagship model focused on long context, multimodal understanding, and flexible deployment.',
      bio:
        'Llama 4 is a strong fit for teams that value open-weight deployment options, custom infrastructure, or experimentation outside purely hosted model ecosystems. It is well suited to long-context work, multimodal use cases, and enterprise settings that benefit from customization.',
      strengths: [
        'Open-weight flexibility',
        'Long-context capability',
        'Multimodal potential',
        'Custom deployment friendliness'
      ],
      bestFor: [
        'Custom deployments',
        'Long-document analysis',
        'Multimodal apps',
        'Research and tuning'
      ]
    },
    deepseek: {
      id: 'deepseek',
      name: 'DeepSeek R1',
      vendor: 'DeepSeek',
      shortBio:
        'A reasoning-oriented model focused on structured thinking, logic-heavy tasks, and technical precision.',
      bio:
        'DeepSeek R1 is aimed more directly at reasoning-intensive tasks than a general-purpose assistant model. It is a good option for math, logic, debugging, and technical analysis where users want more explicit structure and a stronger “reasoning-first” style of output.',
      strengths: [
        'Reasoning-first output style',
        'Strong logical structure',
        'Good debugging support',
        'High technical precision'
      ],
      bestFor: [
        'Math and logic',
        'Debugging',
        'Technical analysis',
        'Structured problem solving'
      ]
    },
    commandLight: {
      id: 'commandLight',
      name: '⌘ Instruct',
      vendor: 'Cohere',
      shortBio:
        'A lightweight Cohere Command model tuned for fast, cost-efficient enterprise chat and grounded short answers.',
      bio:
        'Command Instruct is suited to high-volume assistants, quick Q&A, and workflows where low latency matters more than maximum reasoning depth. It pairs well with structured business prompts and retrieval-style tasks.',
      strengths: [
        'Low-latency replies',
        'Efficient everyday chat',
        'Grounded, instruction-friendly tone',
        'Strong fit for enterprise assistants'
      ],
      bestFor: [
        'Fast customer support drafts',
        'Short summaries and rewrites',
        'High-throughput chat',
        'Lightweight RAG-style answers'
      ]
    },
    jamba: {
      id: 'jamba',
      name: 'Jamba Large',
      vendor: 'AI21 Labs',
      shortBio:
        'AI21 Labs enterprise model designed for long context, structured outputs, and practical workflow orchestration.',
      bio:
        'Jamba Large is built for document-heavy and operations-focused workloads where context length, consistent structure, and reliable instruction following matter. It is a strong fit for enterprise copilots, long-form analysis, and multi-step business workflows.',
      strengths: [
        'Long-context handling',
        'Structured enterprise output',
        'Reliable instruction following',
        'Workflow-friendly reasoning'
      ],
      bestFor: [
        'Long-document analysis',
        'Enterprise assistant workflows',
        'Structured summaries',
        'Operations and policy tasks'
      ]
    },
    scout: {
      id: 'scout',
      name: 'Meta Scout',
      vendor: 'Meta',
      shortBio:
        'A lighter-weight Llama 4 variant built for speed, efficient inference, and practical everyday workloads.',
      bio:
        'Meta Scout is best thought of as a faster, lighter open-weight option for teams that still want modern model capabilities without the heaviest inference footprint. It fits rapid Q&A, lightweight coding help, and quick-turnaround assistant tasks.',
      strengths: [
        'Lower-latency responses',
        'Efficient inference',
        'Open-weight deployment options',
        'Useful practical chat quality'
      ],
      bestFor: [
        'Rapid Q&A',
        'Lightweight coding help',
        'Fast prototyping',
        'Private or local deployments'
      ]
    },
    haiku45: {
      id: 'haiku45',
      name: 'Claude Haiku 4.5',
      vendor: 'Anthropic',
      shortBio:
        'Fast Claude model for quick responses, concise output, vision+text prompts, and high-throughput usage.',
      bio:
        'Claude Haiku 4.5 is ideal when responsiveness matters most. It balances speed and capability for multimodal and text workflows—quick summaries, rewrites, image understanding, and day-to-day support where low latency is important.',
      strengths: [
        'Very fast responses',
        'Clear concise output',
        'Cost-efficient scaling',
        'Reliable instruction following'
      ],
      bestFor: [
        'High-volume chatbots',
        'Quick summaries',
        'Rewrites and cleanup',
        'Responsive assistant workflows'
      ]
    },
    stability: {
      id: 'stability',
      name: 'Stability 3.5',
      vendor: 'Stability',
      shortBio:
        'A detail-focused image generation model known for prompt fidelity, texture, and strong visual consistency.',
      bio:
        'Stability 3.5 is well suited to photoreal and design-oriented image generation where you want crisp results and solid prompt adherence. It performs especially well when iterating on a look, producing product visuals, or generating polished scenes with controlled style.',
      strengths: [
        'Strong image detail',
        'Reliable prompt adherence',
        'Good consistency across variations',
        'Flexible style control'
      ],
      bestFor: [
        'Product imagery',
        'Marketing visuals',
        'Photoreal scenes',
        'Iterative design generation'
      ]
    },
    stability35l: {
      id: 'stability35l',
      name: 'Stability 3.5 Large',
      vendor: 'Stability',
      shortBio:
        'A large-format Stability model tuned for higher detail retention, prompt accuracy, and production-ready image quality.',
      bio:
        'Stability 3.5 Large is designed for high-fidelity image generation when you need better detail depth and cleaner prompt alignment at output. It is a strong fit for polished campaign art, product visuals, and repeatable brand-forward creative workflows.',
      strengths: [
        'High-fidelity visual detail',
        'Strong prompt alignment',
        'Consistent production quality',
        'Reliable creative iteration'
      ],
      bestFor: [
        'Campaign-ready visuals',
        'Product mockups',
        'Brand-safe creative assets',
        'High-detail image generation'
      ]
    },
    dale: {
      id: 'dale',
      name: 'DALL·E 3',
      vendor: 'OpenAI',
      shortBio:
        'A polished image model known for translating natural-language prompts into cohesive, finished compositions.',
      bio:
        'DALL·E 3 is a strong creative default when you want prompt-driven imagery with good composition and a polished overall feel. It is especially helpful for concept work, marketing art, storytelling scenes, and turning detailed written ideas into finished visuals quickly.',
      strengths: [
        'Strong prompt interpretation',
        'Polished visual composition',
        'Good creative translation',
        'Consistent aesthetic quality'
      ],
      bestFor: [
        'Creative illustrations',
        'Campaign art',
        'Concept visualization',
        'Story-driven image generation'
      ]
    },
    dale2legacy: {
      id: 'dale2legacy',
      name: 'DALL·E 2',
      vendor: 'OpenAI',
      shortBio:
        'A legacy image model that remains useful for older edit and variation workflows.',
      bio:
        'DALL·E 2 remains relevant in environments that depend on older image pipelines or compatibility with existing tools. It can still be useful for simpler generations, quick variations, and maintaining consistency with older creative projects.',
      strengths: [
        'Legacy workflow compatibility',
        'Simple generation behavior',
        'Useful variation support',
        'Predictable outputs'
      ],
      bestFor: [
        'Legacy image workflows',
        'Simple prompt-to-image tasks',
        'Older project compatibility',
        'Lightweight visual generation'
      ]
    },
    geminiproimage: {
      id: 'geminiproimage',
      name: 'Gemini 3',
      vendor: 'Google',
      shortBio:
        'A Google image model designed for clean generations, controlled edits, and reference-guided workflows.',
      bio:
        'Gemini Pro Image is especially useful when you want a controlled image workflow rather than pure one-shot creativity. It fits reference-based generation, iterative editing, product visuals, and brand-conscious design work where consistency matters.',
      strengths: [
        'Reference-guided generation',
        'Strong edit workflows',
        'Clean, consistent outputs',
        'Good control over variations'
      ],
      bestFor: [
        'Reference-based generation',
        'Image editing',
        'Product mockups',
        'Brand-safe visual work'
      ]
    },
    veo31: {
      id: 'veo31',
      name: 'Veo 3.1',
      vendor: 'Google',
      shortBio:
        'A cinematic video model with an emphasis on camera motion, scene detail, and film-like output quality.',
      bio:
        'Veo 3.1 is a strong choice for polished video ideation when you care about shot language and camera feel. It is well suited to cinematic marketing concepts, mood reels, and prompt-driven clips where visual style and movement matter.',
      strengths: [
        'Strong camera movement',
        'Film-like scene detail',
        'Stable visual coherence',
        'Good cinematic prompt handling'
      ],
      bestFor: [
        'Cinematic clips',
        'Marketing concepts',
        'Mood reels',
        'Shot-based creative experiments'
      ]
    },
    cogvideox3: {
      id: 'cogvideox3',
      name: 'CogVideoX-3',
      vendor: 'CogX',
      shortBio:
        'A stylized video model geared toward expressive motion and creative experimentation.',
      bio:
        'CogVideoX-3 is most useful when you want to explore motion-heavy or stylized video outputs rather than purely cinematic realism. It is a strong option for experimental sequences, dynamic concept work, and rapid motion look-development.',
      strengths: [
        'Expressive motion',
        'Bold stylization',
        'Good creative experimentation',
        'Useful look development'
      ],
      bestFor: [
        'Experimental video concepts',
        'Stylized clips',
        'Motion studies',
        'Creative exploration'
      ]
    },
    cogx: {
      id: 'cogx',
      name: 'CogX',
      vendor: 'CogX',
      shortBio:
        'A practical video generation option focused on quick iterations and dependable motion results.',
      bio:
        'CogX fits rapid prompt testing and early-stage video prototyping where you want to generate multiple motion-forward options quickly. It is useful for iteration-heavy creative workflows rather than a single high-polish final render path.',
      strengths: [
        'Fast iteration',
        'Dependable motion generation',
        'Good for multiple variants',
        'Useful for prototyping'
      ],
      bestFor: [
        'Prompt A/B testing',
        'Motion-forward prototypes',
        'Multiple clip variants',
        'Early creative exploration'
      ]
    }
  };

  selectedModel?: CatalogModel;

  private readonly vendorImages: Record<string, string> = {
    Anthropic: '/assets/logos/anthropic.svg',
    OpenAI: '/assets/logos/openai.svg',
    Google: '/assets/logos/google.svg',
    'Zhipu AI': '/assets/logos/zai.svg',
    Writer: '/assets/logos/x5-baby.png',
    Meta: '/assets/logos/meta.svg',
    Mistral: '/assets/logos/mistral.svg',
    DeepSeek: '/assets/logos/deepseek.svg',
    Cohere: '/assets/r.svg',
    xAI: '/assets/logos/xai.svg',
    Stability: '/assets/stability_dark.svg',
    CogX: '/assets/logos/zai.svg',
    'AI21 Labs': '/assets/logos/ai21.png',
    Amazon: '/assets/logos/amazon.svg',
    'Moonshot AI': '/assets/logos/moonshot.svg',
    Qwen: '/assets/logos/iamclari2.png',
    TwelveLabs: '/assets/logos/iamclari2.png'
  };

  constructor(
    public dialogRef: MatDialogRef<AiCatalogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      categories: CatalogCategory[];
      selectedModel?: string;
      selectedModelName?: string;
      userEmail?: string | null;
    },
    private toast: ToastService,
    private reasoningService: ReasoningService,
    private chatFire: ChatFireService,
    private router: Router
  ) {
    const incomingCategories = data?.categories || [];
    const rawBaseCategories = incomingCategories.length ? incomingCategories : this.defaultCategories;
    const baseCategories = this.normalizeChatGptCategories(rawBaseCategories);

    this.categories = this.sortCategoriesAlphabetically(this.filterRemovedModels(baseCategories));
    this.initialSelectedValue = this.normalizeChatGptValue(
      this.isRemovedModelValue(data?.selectedModel) ? undefined : data?.selectedModel
    );

    const currentValue = this.normalizeChatGptValue(data?.selectedModel);
    if (currentValue && !this.isRemovedModelValue(currentValue)) {
      const found = this.categories
        .flatMap(category => category.models)
        .find(model => model.value === currentValue);

      if (found) {
        this.selectedModel = found;
      }
    }

    this.selectedGpt52ReasoningLevel = this.getInitialGpt52ReasoningLevel();
  }

  ngOnInit(): void {
    this.authSub = this.chatFire.isAuthenticated$.subscribe((v) => (this.isSignedInUser = v));
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    const container = document.querySelector('.mega-menu-container') as HTMLElement | null;
    const bar = this.selectionBar?.nativeElement;

    if (!container || !bar) {
      return;
    }

    let t: any;

    container.addEventListener(
      'scroll',
      () => {
        bar.classList.add('scrolling');
        clearTimeout(t);
        t = setTimeout(() => bar.classList.remove('scrolling'), 120);
      },
      { passive: true }
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  choose(model: CatalogModel): void {
    if (this.isRemovedModel(model)) {
      return;
    }

    this.selectedModel = model;

    if (this.isChatGpt52(model)) {
      this.selectedGpt52ReasoningLevel = 'instant';
      this.reasoningService.setReasoningLevel('instant', 'chatgpt52');
    }
  }

  apply(): void {
    if (!this.selectedModel) {
      this.toast.show('Please select a model first.', 'warning');
      return;
    }

    if (!this.isSignedInUser && isClariSignedInOnlyCatalogModel(this.selectedModel.value)) {
      this.toast.show('Please sign in to use this model.', 'info');
      return;
    }

    this.dialogRef.close({
      name: this.getModelName(this.selectedModel),
      value: this.selectedModel.value
    });
  }

  showPremiumForSelection(): boolean {
    return !!this.selectedModel && !this.isSignedInUser && isClariSignedInOnlyCatalogModel(this.selectedModel.value);
  }

  showComingSoonForSelection(): boolean {
    if (this.isClariVideoCatalogContext()) {
      return !!this.selectedModel;
    }
    return this.isClariImageCatalogContext() && this.normalizeChatGptValue(this.selectedModel?.value) === 'geminiproimage';
  }

  onComingSoonClick(): void {
    this.toast.show('Gemini Image is coming soon for this chat.', 'info');
  }

  onPremiumClick(): void {
    this.toast.show('Sign in to use premium models.', 'info');
    this.dialogRef.close();
    void this.router.navigate(['/auth', 'log-in']);
  }

  isSelected(model: CatalogModel): boolean {
    return this.normalizeChatGptValue(this.selectedModel?.value) === this.normalizeChatGptValue(model.value);
  }

  get hasPendingSelection(): boolean {
    return !!this.selectedModel && this.selectedModel.value !== this.initialSelectedValue;
  }

  isChatGpt52(model?: CatalogModel): boolean {
    return this.normalizeChatGptValue(model?.value) === 'chatgpt';
  }

  get visibleGpt52ReasoningLevel(): ChatGpt52ReasoningLevel {
    return this.selectedGpt52ReasoningLevel;
  }

  isGpt52ReasoningChecked(value: ChatGpt52ReasoningLevel): boolean {
    return this.visibleGpt52ReasoningLevel === value;
  }

  selectGpt52ReasoningEffort(value: ChatGpt52ReasoningLevel): void {
    this.selectedGpt52ReasoningLevel = value;
    this.reasoningService.setReasoningLevel(value, 'chatgpt52');
  }

  get catalogModels(): CatalogModel[] {
    return this.currentModels;
  }

  get currentModels(): CatalogModel[] {
    return this.partitionCatalogModels().currentModels;
  }

  get sidelinedModels(): CatalogModel[] {
    return this.partitionCatalogModels().sidelinedModels;
  }

  private partitionCatalogModels(): { currentModels: CatalogModel[]; sidelinedModels: CatalogModel[] } {
    const seen = new Set<string>();
    const currentModels: CatalogModel[] = [];
    const sidelinedModels: CatalogModel[] = [];

    this.categories.forEach(category => {
      category.models.forEach(model => {
        if (this.isRemovedModel(model) || seen.has(model.value)) {
          return;
        }

        seen.add(model.value);
        if (this.isSidelinedModel(model)) {
          sidelinedModels.push(model);
        } else {
          currentModels.push(model);
        }
      });
    });

    const byName = (a: CatalogModel, b: CatalogModel) => this.getModelName(a).localeCompare(this.getModelName(b));
    return {
      currentModels: currentModels.sort(byName),
      sidelinedModels: sidelinedModels.sort(byName)
    };
  }

  getModelBio(model: CatalogModel): CatalogModelDetails {
    const bioKey = this.normalizeChatGptValue(model.value);
    return (
      this.modelCatalog[bioKey] || {
        id: bioKey || model.value,
        name: this.getModelName(model),
        vendor: 'AI Model',
        shortBio: `${this.getModelName(model)} is designed for everyday reasoning and helpful responses.`,
        bio: `${this.getModelName(model)} is a capable model designed to support everyday reasoning, writing, and analysis tasks. It offers balanced performance for general-purpose use.`,
        strengths: ['General reasoning', 'Clear responses', 'Flexible output', 'Everyday productivity'],
        bestFor: ['General Q&A', 'Summaries', 'Drafting', 'Task assistance']
      }
    );
  }

  private getCatalogDetailsByValue(value?: string | null): CatalogModelDetails | undefined {
    const normalized = this.normalizeChatGptValue(value);
    return normalized ? this.modelCatalog[normalized] : undefined;
  }

  getModelImage(model: CatalogModel): string {
    const bio = this.getModelBio(model);
    return this.vendorImages[bio.vendor] || '/assets/llm/summaryclaude.jpg';
  }

  private getInitialGpt52ReasoningLevel(): ChatGpt52ReasoningLevel {
    const level = this.reasoningService.getReasoningLevel();
    return level === 'auto' ? 'instant' : level;
  }

  private sortCategoriesAlphabetically(categories: CatalogCategory[]): CatalogCategory[] {
    return categories.map(category => ({
      ...category,
      models: [...category.models].sort((a, b) => this.getModelName(a).localeCompare(this.getModelName(b)))
    }));
  }

  private filterRemovedModels(categories: CatalogCategory[]): CatalogCategory[] {
    return categories
      .map(category => ({
        ...category,
        models: (category.models || []).filter(model => !this.isRemovedModel(model))
      }))
      .filter(category => category.models.length > 0);
  }

  private isRemovedModel(model?: CatalogModel): boolean {
    if (!model) {
      return false;
    }

    return this.isRemovedModelValue(model.value) || this.isRemovedModelName(this.getModelName(model));
  }

  private isRemovedModelValue(value?: string | null): boolean {
    return this.removedModelValues.has((value || '').trim().toLowerCase());
  }

  private isRemovedModelName(name?: string | null): boolean {
    const normalizedName = this.normalizeModelText(name);
    return this.removedModelNameMatchers.some(matcher => normalizedName.includes(matcher));
  }

  private isSidelinedModel(model?: CatalogModel): boolean {
    if (!model) {
      return false;
    }
    const normalizedValue = this.normalizeChatGptValue(model.value);
    const normalizedName = this.normalizeModelText(this.getModelName(model));
    return (
      this.sidelinedModelValues.has(normalizedValue) ||
      this.sidelinedModelNameMatchers.some(matcher => normalizedName.includes(matcher))
    );
  }

  private normalizeModelText(value?: string | null): string {
    return (value || '')
      .toLowerCase()
      .replace(/[._·+-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizeChatGptValue(value?: string | null): string {
    const normalized = (value || '').trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    if (['chatgpt52', 'chatgpt54', 'chatgpt5', 'chatgpt4', 'chatgpt'].includes(normalized)) {
      return 'chatgpt';
    }

    if (normalized === 'opus6') {
      return 'opus46';
    }
    if (normalized === 'opus7') {
      return 'opus47';
    }
    if (['haiku', 'haiku35', 'haiku3', 'haiku3.5', 'haiku4', 'haiku4.5'].includes(normalized)) {
      return 'haiku45';
    }

    return normalized;
  }

  private normalizeChatGptCategories(categories: CatalogCategory[]): CatalogCategory[] {
    return (categories || []).map(category => ({
      ...category,
      models: (category.models || []).map(model => {
        const normalizedValue = this.normalizeChatGptValue(model?.value);
        const canonical = this.getCatalogDetailsByValue(normalizedValue);
        return {
          ...model,
          value: normalizedValue || model.value,
          name: canonical?.name || model?.name || model?.label,
          label: canonical?.name || model?.label || model?.name
        };
      })
    }));
  }

  private isClariImageCatalogContext(): boolean {
    const context = String((this.data as any)?.context || '').trim().toLowerCase();
    return context === 'clariimagechat';
  }

  private isClariVideoCatalogContext(): boolean {
    const context = String((this.data as any)?.context || '').trim().toLowerCase();
    return context === 'clarivideochat';
  }
}
